"use client";

import { ComponentProps, PropsWithChildren } from "react";
import { Slot, Text as RadixText } from "@radix-ui/themes";
import { useIsShopsite } from "@context/DomainContext";
import { joinClassNames } from "@utils/helpers";

interface Props {
  sharedClasses?: string;
  shopsiteClasses?: string;
  whitelabelClasses?: string;
}

export default function Text({
  className = "",
  sharedClasses = "",
  shopsiteClasses = "",
  whitelabelClasses = "",
  asChild,
  children,
  ref,
  ...restProps
}: ComponentProps<typeof RadixText> & PropsWithChildren<Props>) {
  const isShopsite = useIsShopsite();

  if (isShopsite) {
    if (asChild) {
      return (
        <Slot
          ref={ref}
          className={joinClassNames(className, sharedClasses, shopsiteClasses)}
          {...restProps}
        >
          {children}
        </Slot>
      );
    }

    switch (restProps.as) {
      case "p":
        return (
          <p
            ref={ref as React.Ref<HTMLParagraphElement>}
            className={joinClassNames(
              className,
              sharedClasses,
              shopsiteClasses
            )}
            {...restProps}
          >
            {children}
          </p>
        );
      case "label":
        return (
          <label
            ref={ref as React.Ref<HTMLLabelElement>}
            className={joinClassNames(
              className,
              sharedClasses,
              shopsiteClasses
            )}
            htmlFor={restProps.htmlFor}
            {...restProps}
          >
            {children}
          </label>
        );
      case "div":
        return (
          <div
            ref={ref as React.Ref<HTMLDivElement>}
            className={joinClassNames(
              className,
              sharedClasses,
              shopsiteClasses
            )}
            {...restProps}
          >
            {children}
          </div>
        );
      default:
        return (
          <span
            ref={ref}
            className={joinClassNames(
              className,
              sharedClasses,
              shopsiteClasses
            )}
            {...restProps}
          >
            {children}
          </span>
        );
    }
  }

  return (
    <RadixText
      ref={ref}
      asChild={asChild}
      {...restProps}
      className={joinClassNames(className, sharedClasses, whitelabelClasses)}
    >
      {children}
    </RadixText>
  );
}
